import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';

import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { useSelector } from 'react-redux';
import {
  selectEmployeeData,
  selectAdjustHoursReportData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearAdjustHours,
  getEmployeeList,
  getAdjustHoursReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import moment from 'moment';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import ReusableGrid from '../../../components/Grid';
import useAdjustHoursReportColumns from './hooks/useAdjustHoursReportColumns';
import useAdjustHoursReportRows from './hooks/useAdjustHoursReportRows';
import { useForm } from 'react-hook-form';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const FORMAT_DATE_STRING = 'YYYY-MM-DD';

const AdjustHoursReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { adjustHours, isLoadingAdjustHours } = useSelector(
    selectAdjustHoursReportData
  );
  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: { id: 'ALL', name: 'All' },
      pay_codes: [],
      reasons: [],
    },
  });

  const { fromDate, toDate, render } = useDateRangeFilter();
  const { emp_code: employees } = watch();

  const reqParams = useMemo(
    () => ({
      start_dt: moment(fromDate).format(FORMAT_DATE_STRING) + ' 00:00:00',
      end_dt: moment(toDate).format(FORMAT_DATE_STRING) + ' 23:59:59',
      sel_emp: employees.id || 'ALL',
    }),
    [employees.id, fromDate, toDate]
  );

  const handleFindClick = () => dispatch(getAdjustHoursReportData(reqParams));

  useEffect(
    () => {
      dispatch(getEmployeeList());
      dispatch(getAdjustHoursReportData(reqParams));
      return () => dispatch(clearAdjustHours());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columns = useAdjustHoursReportColumns();
  const rows = useAdjustHoursReportRows(adjustHours.data, columns);

  const rawFilterConfig = [
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
          enableAllOption={true}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Adjust Hour Report Details" />

      <Box container xs={12} className="d-flex justify-content-between">
        <Box sx={{ width: '100%', mr: 2 }}>
          <Box container spacing={2}>
            <Grid container sx={{ width: '100%', mb: '24px' }}>
              <FilterPanel filters={filterConfig} columns={3} />
            </Grid>
            <Grid container sx={{ width: '100%', mb: '24px' }} spacing={2}>
              {render}
            </Grid>
          </Box>
        </Box>
      </Box>

      {isLoadingAdjustHours ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {!!rows.length ? (
            <Box mt={4}>
              {rows.map((item) => (
                <Box key={item.name} mb={4}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {item.name}
                  </Typography>
                  <ReusableGrid columns={columns} rows={item.items} footer={item.footer} />
                </Box>
              ))}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
    </Paper>
  );
};

export default AdjustHoursReport;
